import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import BasicFilter from '../../../global/components/table/helpers/filters/BasicFilter'
import DateFilter from '../../../global/components/table/helpers/filters/DateFilter'
import WorkOrderDetailModal from '../work-order-detail/components/main/WorkOrderDetailModal'
import { WoDetailContext } from '../context/context'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import createSearchParamsString from '../../../global/utils/createSearchParamsString'
import { IOrder } from '../../../../../app/entities/Order'
import { ILocationFilters } from '../main/WorkOrdersMain'
import removeDoubleCommas from '../../../global/utils/removeDoubleCommas'
import { ITechnician } from '../../../../../app/entities/Technician'
import {
  DELIVERY_STATUSES_MAP,
  STATUSES,
} from '../../../global/constants/order-status'
import isDeliveryByOrderService from '../../../global/utils/is-delivery-by-order-service'
import { SERVICE_NAMES } from '../../../global/constants/service'
import { getAllTireRackOrderNumbers } from '../../../global/utils/tire-rack-orders'
import fetchDataAndSetTable from '../../../global/components/table/helpers/fetchDataAndSetTable'
import LockIcon from '@mui/icons-material/Lock'
import DropdownFilter from '../../../global/components/table/helpers/filters/DropdownFilter'
import LocationFilter from '../../../global/components/table/helpers/filters/LocationFilter'
import { TIME_SLOT_TYPES } from '../../../global/constants/time-slot'

interface IOrderWithTechnician extends IOrder {
  technician?: Pick<ITechnician, 'objectId' | 'name'> | null
}

interface IWorkOrdersTableProps {
  locationFilters: ILocationFilters
}

function capitalizeStatus(status: string): string {
  return status
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function formatDate(dateValue: string): string {
  if (!dateValue || dateValue.length === 0) return ''
  const [year, month, day] = dateValue.split('-')
  return `${month}/${day}/${year.slice(-2)}`
}
function WorkOrdersTable(props: IWorkOrdersTableProps) {
  const { locationFilters } = props
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [tableState, setTableState] = useState({
    data: [] as IOrderWithTechnician[],
    isLoading: false,
    count: 0,
    pageSize: 25,
    offSet: 0,
  })
  const [timeSlotFilter, setTimeSlotFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [serviceApptFilter, setServiceApptFilter] = useState('')
  const [locationColumnFilter, setLocationColumnFilter] = useState('')
  const [columnFilters, setColumnFilters] = useState({})

  // Build status dropdown items with both installation and delivery statuses
  const installationStatusItems = Object.values(STATUSES).map((status) => ({
    key: `installation:${status}`,
    value: capitalizeStatus(status),
  }))

  const deliveryStatusItems = Object.entries(DELIVERY_STATUSES_MAP)
    .filter(
      ([, displayValue], index, self) =>
        // Remove duplicates by display value
        self.findIndex(([, v]) => v === displayValue) === index,
    )
    .map(([, displayValue]) => ({
      key: `delivery:${displayValue}`,
      value: capitalizeStatus(displayValue),
    }))

  const statusItems = [...installationStatusItems, ...deliveryStatusItems]
  const { queryOrderData, clearData } = useContext(WoDetailContext)

  async function handleOpen(id: string) {
    setIsModalOpen(true)
    await queryOrderData(id)

    const queryStringParams = await createSearchParamsString(searchParams)
    const url = `/work-orders/${id}` + queryStringParams
    navigate(url)
  }

  function handleClosed() {
    setIsModalOpen(false)
    navigate('/work-orders')
    clearData()
    retrieveTableData()
  }
  function onRecordClickHandler(row: any) {
    const {
      original: { objectId },
    } = row
    handleOpen(objectId)
  }

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  useEffect(() => {
    if (id && !isModalOpen) {
      handleOpen(id)
    }
  }, [id])

  const getAllOrdersTableEndpoint = `${process.env.REACT_APP_ORIGIN}/orders/getAllOrders`
  function retrieveTableData() {
    fetchDataAndSetTable<IOrderWithTechnician>({
      endpoint: getAllOrdersTableEndpoint,
      queries: [
        { columnFilters: JSON.stringify(columnFilters) },
        { selectedLocationId: locationFilters.locationFilter },
        { pageSize: tableState.pageSize.toString() },
        { offSet: tableState.offSet.toString() },
        { sortBy: '`workOrderId` DESC' },
      ],
      setTableState,
    })
  }

  useEffect(() => {
    retrieveTableData()
  }, [columnFilters, locationFilters, tableState.pageSize, tableState.offSet])

  const columns: Column<IOrderWithTechnician | any>[] = [
    {
      Header: 'Order ID',
      accessor: 'workOrderId',
      Filter: BasicFilter({
        onChange: (val: string) => onColumnFilterUpdate({ workOrderId: val }),
      }),
    },
    {
      Header: 'TR Order ID',
      accessor: 'tireRackOrderNumbers',
      Cell: ({ row: { original } }) => {
        const allTireRackOrderNumbers = getAllTireRackOrderNumbers(original)
        if (allTireRackOrderNumbers.length > 0) {
          return <div>{allTireRackOrderNumbers.join(', ')}</div>
        } else {
          return <div></div>
        }
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackOrders: {
              orderNumber: val,
            },
          }),
      }),
    },
    {
      Header: 'Location',
      accessor: 'location.name',
      Filter: LocationFilter({
        value: locationColumnFilter,
        onChange: (val: string) => {
          setLocationColumnFilter(val)
          onColumnFilterUpdate({ location: { name: val } })
        },
      }),
    },
    {
      Header: 'Technician Name',
      Cell: ({ row: { original } }) => {
        const { routeInstructions, technician, lockedTechnician } =
          original as IOrderWithTechnician

        let technicianName: string = ''
        let isLocked = false

        const routeInstruction = routeInstructions?.[0]

        if (lockedTechnician?.name) {
          isLocked = true
        }
        if (routeInstruction?.route?.technician?.name) {
          // if already routed
          technicianName = routeInstruction?.route?.technician?.name
        } else if (technician?.name) {
          // if only planned
          technicianName = technician?.name
        }

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {isLocked && (
              <div>
                <LockIcon fontSize='small' />
              </div>
            )}
            {technicianName}
          </div>
        )
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            routeArea: {
              technician: {
                name: val,
              },
            },
          }),
      }),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row: { original } }) => {
        const { status, orderServices } = original as IOrderWithTechnician
        const isDelivery = isDeliveryByOrderService(orderServices || [])
        if (isDelivery) {
          return (
            <div>
              {capitalizeStatus(DELIVERY_STATUSES_MAP[status || ''] || status || '')}
            </div>
          )
        } else {
          return <div>{capitalizeStatus(status || '')}</div>
        }
      },
      Filter: DropdownFilter({
        value: statusFilter,
        onChange: (val: string) => {
          setStatusFilter(val)
          if (val.startsWith('delivery:')) {
            // Delivery status selected - map display value back to installation status
            const displayValue = val.replace('delivery:', '')
            const mappedStatus = Object.keys(DELIVERY_STATUSES_MAP).find(
              (key) => DELIVERY_STATUSES_MAP[key] === displayValue,
            )
            onColumnFilterUpdate({
              status: mappedStatus || '',
              orderServices: {
                service: { name: SERVICE_NAMES.TIRE_DELIVERY },
              },
            })
          } else if (val.startsWith('installation:')) {
            // Installation status selected
            const status = val.replace('installation:', '')
            onColumnFilterUpdate({
              status: status,
              orderServices: {
                service: '', // Remove delivery filter
              },
            })
          } else {
            // Empty value - clear filter
            onColumnFilterUpdate({
              status: '',
              orderServices: {
                service: '',
              },
            })
          }
        },
        items: statusItems,
      }),
    },
    {
      Header: 'Service Appt',
      accessor: 'timeSlot.fromDateGenerated',
      Filter: DateFilter({
        value: serviceApptFilter,
        onChange: (val: string) => {
          setServiceApptFilter(val)
          onColumnFilterUpdate({
            timeSlot: {
              fromDateGenerated: formatDate(val),
            },
          })
        },
      }),
      sortInverted: true,
    },
    {
      Header: 'Time Slot',
      accessor: 'timeSlot.type',
      Cell: ({ row: { original } }) => {
        const { timeSlot } = original as IOrderWithTechnician
        return <div>{capitalizeStatus(timeSlot?.type || '')}</div>
      },
      Filter: DropdownFilter({
        value: timeSlotFilter,
        onChange: (val: string) => {
          setTimeSlotFilter(val)
          onColumnFilterUpdate({
            timeSlot: {
              type: val,
            },
          })
        },
        items: Object.values(TIME_SLOT_TYPES).map((type) => ({
          key: type,
          value: capitalizeStatus(type),
        })),
      }),
    },
    {
      Header: 'First Name',
      accessor: 'customer.firstName',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { firstName: val } }),
      }),
    },
    {
      Header: 'Last Name',
      accessor: 'customer.lastName',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { lastName: val } }),
      }),
    },
    {
      Header: 'Customer Email',
      accessor: 'customer.email',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { email: val } }),
      }),
    },
    {
      Header: 'Customer Phone',
      accessor: 'customer.trimmedPhoneNumber',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { trimmedPhoneNumber: val } }),
      }),
    },
    {
      Header: 'Installation Address',
      accessor: 'installationPoint.fullAddress',
      Cell: ({ row: { original } }) => {
        const { installationPoint } = original as IOrderWithTechnician
        if (installationPoint) {
          return <div>{removeDoubleCommas(installationPoint.fullAddress)}</div>
        } else {
          return <div></div>
        }
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ installationPoint: { fullAddress: val } }),
      }),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <WorkOrderDetailModal
        handleClosed={handleClosed}
        isOpen={isModalOpen}
      />
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={onRecordClickHandler}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default WorkOrdersTable
